import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ActionsModal, Icon, RenderWhen } from "../../../../../../components";
import { isValidSha1Hash } from "../../../../../../utils";

import { ActionsModalNames } from "../../../../../../components/ActionsModal";
import TagsEditSingle from "../../../../../../components/tags/containers/TagsEditSingle";
import { useDataModal } from "../../../../../../hooks";
import useModal from "../../../../../../hooks/useModal";
import { route, settings } from "../../../../../../shim/jsbridge";
import {
  useCopyToMySubmissions,
  useLocalSampleSummaryParams
} from "../../../../hooks";
import { SAMPLE_REPORT_KEY } from "../../../../hooks/useGetReportSummary";
import PdfItem from "./PdfItem";
import ShortPDFItem from "./ShortPDFItem";
import ReportSummaryContainerPDFExport from "../../../../containers/report_summary/ReportSummaryContainerPDFExport";

const { FEATURE_ALERTFEED } = settings;

const { userName, isSuperuser } = window.tcbase;

type Props = {
  sourceSha1?: string | null;
  sourceFileName: string;
  fileName: string;
  uploader?: string | null;
  extractedFileCount?: number;
  onClassificationChange?: () => void;
};

const Actions = ({
  sourceSha1,
  sourceFileName,
  uploader,
  fileName,
  extractedFileCount,
  onClassificationChange
}: Props) => {
  const navigate = useNavigate();
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const queryClient = useQueryClient();
  const { data, onToggle } = useDataModal();
  const [isTagsModalOpen, _, toggleTagsModal] = useModal(false);
  const { mutate: copyToSubmissions } = useCopyToMySubmissions();
  const [isDownloadingShortPDF, setIsDownloadingShortPDF] = useState(false);

  const isUploader = uploader === userName;
  const isExtractedFile = Boolean(relationshipId);
  const canDelete = (isUploader || isSuperuser) && !isExtractedFile;
  const isValidSha1 = isValidSha1Hash(hash);

  const refetchReport = () => {
    queryClient.refetchQueries(
      { queryKey: [SAMPLE_REPORT_KEY] },
      { cancelRefetch: false }
    );
  };

  const handleDownloadSample = () => {
    onToggle(
      { sha1: hash, file_names: [fileName] },
      ActionsModalNames.Download
    );
  };

  const handleSetClassification = () => {
    onToggle({ sha1: hash }, ActionsModalNames.UserClassification);
  };

  const handleSubscribe = () => {
    const subscriptionData = {
      subscriptionHashes: [{ sha1: hash, available: true, location: "local" }],
      action: "subscribe"
    };
    onToggle(subscriptionData, ActionsModalNames.Subscribehashes);
  };

  const handleUnsubscribe = () => {
    const unsubscriptionData = {
      subscriptionHashes: [hash],
      action: "unsubscribe"
    };
    onToggle(unsubscriptionData, ActionsModalNames.Unsubscribehashes);
  };

  const handleCopyToSubmissions = () => {
    if (!sourceId) return;
    copyToSubmissions({
      fileName,
      sourceId,
      userName
    });
  };

  const handleClassificationChange = () => {
    setTimeout(() => {
      refetchReport();
      onClassificationChange?.();
    }, 10000);
  };

  const handleDownloadExtractedFiles = () => {
    const downloadData = {
      sha1: hash,
      file_names: [fileName]
    };
    onToggle(downloadData, ActionsModalNames.DownloadUnpacked);
  };

  const handleDeleteSample = () => {
    const deleteData = {
      hash,
      onSuccess: () => {
        navigate(route("search"));
      }
    };
    onToggle(deleteData, ActionsModalNames.Delete);
  };

  const handleDownloadContainer = () => {
    const downloadData = {
      sha1: sourceSha1,
      file_names: [sourceFileName],
      isContainer: true
    };
    onToggle(downloadData, ActionsModalNames.Download);
  };

  const handleDownloadShortPDF = () => {
    setIsDownloadingShortPDF(true);
  };

  return (
    <>
      <Dropdown id="actions">
        <Dropdown.Toggle data-testid="report_summary_actions_dropdown_toggle">
          ACTIONS{" "}
          <Icon name="chevron-down" className="dropdown-toggle__chevron-icon" />
        </Dropdown.Toggle>
        <Dropdown.Menu renderOnMount data-testid="actions_menu">
          <PdfItem />
          <ShortPDFItem
            onDownload={handleDownloadShortPDF}
            isDownloading={isDownloadingShortPDF}
          />

          <Dropdown.Item
            onClick={handleDownloadSample}
            data-testid="report_summary_action_download_sample"
          >
            Download Sample
          </Dropdown.Item>
          <RenderWhen condition={Boolean(extractedFileCount)}>
            <Dropdown.Item
              onClick={handleDownloadExtractedFiles}
              data-testid="report_summary_action_download_extracted_files"
            >
              Download Extracted Files
            </Dropdown.Item>
          </RenderWhen>
          <RenderWhen condition={Boolean(sourceSha1)}>
            <Dropdown.Item
              onClick={handleDownloadContainer}
              data-testid="report_summary_action_download_container"
            >
              Download Container
            </Dropdown.Item>
          </RenderWhen>
          <Dropdown.Item
            onClick={handleSetClassification}
            data-testid="report_summary_action_set_classification"
          >
            Set Classification
          </Dropdown.Item>
          <RenderWhen condition={!isUploader}>
            <Dropdown.Item
              onClick={handleCopyToSubmissions}
              data-testid="report_summary_action_add_to_submissions"
            >
              Copy To My Submissions
            </Dropdown.Item>
          </RenderWhen>
          <Dropdown.Item
            onClick={toggleTagsModal}
            data-testid="report_summary_action_edit_tags"
          >
            Edit Tags
          </Dropdown.Item>
          <RenderWhen condition={FEATURE_ALERTFEED}>
            <Dropdown.Item
              disabled={!isValidSha1}
              onClick={handleSubscribe}
              data-testid="report_summary_action_subscribe"
            >
              Subscribe
            </Dropdown.Item>
            <Dropdown.Item
              disabled={!isValidSha1}
              onClick={handleUnsubscribe}
              data-testid="report_summary_action_unsubscribe"
            >
              Unsubscribe
            </Dropdown.Item>
          </RenderWhen>
          <RenderWhen condition={canDelete}>
            <Dropdown.Item
              onClick={handleDeleteSample}
              data-testid="report_summary_action_delete_sample"
            >
              Delete Sample
            </Dropdown.Item>
          </RenderWhen>
        </Dropdown.Menu>
      </Dropdown>
      {data.activeModal && (
        <ActionsModal
          modalName={data.activeModal}
          activeModalData={data.activeModalData}
          toggleModal={onToggle}
          onSubmit={handleClassificationChange}
        />
      )}
      <RenderWhen condition={isTagsModalOpen}>
        <TagsEditSingle
          hash={hash ?? ""}
          show={isTagsModalOpen}
          onSubmitCallback={refetchReport}
          handleShow={toggleTagsModal}
        />
      </RenderWhen>
      {isDownloadingShortPDF && (
        <ReportSummaryContainerPDFExport
          onFinishedDownloading={() => setIsDownloadingShortPDF(false)}
        />
      )}
    </>
  );
};

export default Actions;
